/**
 * Logger utility for development/production environment
 * Prevents console.log statements from appearing in production builds
 */

const isDevelopment = (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') || import.meta.env?.MODE === 'development';

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

class Logger {
  private enabled: boolean;

  constructor() {
    this.enabled = isDevelopment;
  }

  private formatMessage(level: LogLevel, message: string, ...args: unknown[]): void {
    if (!this.enabled && level !== 'error') return;

    const timestamp = new Date().toISOString();
    const prefix = isDevelopment ? `[${timestamp.split('T')[1].split('.')[0]}]` : '';

    switch (level) {
      case 'error':
        console.error(`${prefix} ❌`, message, ...args);
        break;
      case 'warn':
        if (this.enabled) console.warn(`${prefix} ⚠️`, message, ...args);
        break;
      case 'info':
        if (this.enabled) console.info(`${prefix} ℹ️`, message, ...args);
        break;
      case 'debug':
        if (this.enabled) console.debug(`${prefix} 🐛`, message, ...args);
        break;
      default:
        if (this.enabled) console.log(`${prefix}`, message, ...args);
    }
  }

  log(message: string, ...args: unknown[]): void {
    this.formatMessage('log', message, ...args);
  }

  info(message: string, ...args: unknown[]): void {
    this.formatMessage('info', message, ...args);
  }

  warn(message: string, ...args: unknown[]): void {
    this.formatMessage('warn', message, ...args);
  }

  error(message: string, ...args: unknown[]): void {
    // Always log errors, even in production
    this.formatMessage('error', message, ...args);
  }

  debug(message: string, ...args: unknown[]): void {
    this.formatMessage('debug', message, ...args);
  }

  // Group console logs
  group(label: string): void {
    if (this.enabled) console.group(label);
  }

  groupEnd(): void {
    if (this.enabled) console.groupEnd();
  }

  // Table for structured data
  table(data: unknown): void {
    if (this.enabled) console.table(data);
  }

  // Performance timing
  time(label: string): void {
    if (this.enabled) console.time(label);
  }

  timeEnd(label: string): void {
    if (this.enabled) console.timeEnd(label);
  }
}

// Export singleton instance
const logger = new Logger();
export default logger;

// Also export individual methods for convenience
export const { log, info, warn, error, debug } = logger;